import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Link as MuiLink,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Description as DescriptionIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon,
  Storefront as StorefrontIcon,
  CreditCard as CreditCardIcon,
  ReceiptLong as ReceiptLongIcon,
  Groups as GroupsIcon,
  Bolt as BoltIcon,
  PersonAdd as PersonAddIcon,
  Assignment as AssignmentIcon,
  CloudUpload as CloudUploadIcon,
  Paid as PaidIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Speed as SpeedIcon,
  Verified as VerifiedIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
  Shield as ShieldIcon,
  VerifiedUser as VerifiedUserIcon,
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { BRAND, SUPPORT_PHONE, SUPPORT_EMAIL, MAIN_SITE_URL } from './brand';

const CONTAINER_SX = { width: '100%', maxWidth: '1200px', mx: 'auto', px: { xs: 2.5, sm: 4 } };
const SECTION_ANCHOR_SX = { scrollMarginTop: '80px' };

const SectionHeading = ({ title, subtitle, light = false }) => (
  <Box sx={{ textAlign: 'center', maxWidth: '680px', mx: 'auto', mb: { xs: 4, md: 6 } }}>
    <Box
      sx={{
        width: 48,
        height: 4,
        borderRadius: 2,
        background: BRAND.gradient,
        mx: 'auto',
        mb: 2,
      }}
    />
    <Typography
      component="h2"
      sx={{
        fontWeight: 800,
        fontSize: { xs: '1.6rem', md: '2.1rem' },
        lineHeight: 1.2,
        letterSpacing: '-0.015em',
        color: light ? '#fff' : BRAND.ink,
        mb: 1.5,
      }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ color: light ? 'rgba(255,255,255,0.78)' : BRAND.body, fontSize: { xs: '15px', md: '17px' } }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

export const LanguagePill = () => {
  const { t, i18n } = useTranslation();
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'en').startsWith('es') ? 'es' : 'en';
  const languages = [
    { code: 'en', label: 'English', short: 'EN' },
    { code: 'es', label: 'Español', short: 'ES' },
  ];

  return (
    <Box
      role="group"
      aria-label={t('common.language')}
      sx={{
        display: 'inline-flex',
        p: '3px',
        borderRadius: '999px',
        border: `1px solid ${BRAND.border}`,
        background: '#fff',
        flexShrink: 0,
      }}
    >
      {languages.map((lang) => {
        const active = currentLang === lang.code;
        return (
          <Box
            key={lang.code}
            component="button"
            type="button"
            aria-pressed={active}
            aria-label={lang.label}
            onClick={() => i18n.changeLanguage(lang.code)}
            sx={{
              border: 0,
              cursor: 'pointer',
              px: { xs: 1.5, sm: 2 },
              py: 0.75,
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: 'inherit',
              color: active ? '#fff' : BRAND.body,
              background: active ? BRAND.gradient : 'transparent',
              boxShadow: active ? '0 2px 6px rgba(172,104,46,0.30)' : 'none',
              transition: 'all 0.2s ease',
              '&:hover': { color: active ? '#fff' : BRAND.copperDark },
              '&:focus-visible': { outline: `2px solid ${BRAND.copperLight}`, outlineOffset: '2px' },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{lang.label}</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{lang.short}</Box>
          </Box>
        );
      })}
    </Box>
  );
};

export const LoginHeader = () => {
  const { t } = useTranslation();
  const navLinks = [
    { href: '#how-it-works', label: t('loginPage.navHowItWorks') },
    { href: '#programs', label: t('loginPage.navPrograms') },
    { href: '#faq', label: t('loginPage.navFaq') },
  ];

  return (
    <>
    <Box
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 20,
        background: '#fff',
        boxShadow: '0 1px 0 rgba(17,24,39,0.04), 0 4px 16px rgba(17,24,39,0.04)',
        borderBottom: `1px solid ${BRAND.border}`,
      }}
    >
      <Box sx={{ ...CONTAINER_SX, display: 'flex', alignItems: 'center', gap: 2, height: { xs: 64, md: 72 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
          <Box
            component="img"
            src="/images/logo-ob.jpg"
            alt="Core Loan Hub"
            sx={{ height: { xs: '38px', md: '46px' }, width: 'auto', display: 'block' }}
          />
          <Box sx={{ display: { xs: 'none', sm: 'block' }, height: 32, width: '1px', background: BRAND.border }} />
          <Box sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1.2 }}>
            <Typography sx={{ color: BRAND.muted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: 1.5 }}>
              {t('loginPage.poweredBy')}
            </Typography>
            <MuiLink
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: BRAND.copperDark, fontWeight: 700, fontSize: '14px', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Core Business Capital
            </MuiLink>
          </Box>
        </Box>

        <Box component="nav" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3.5 }}>
          {navLinks.map((link) => (
            <MuiLink
              key={link.href}
              href={link.href}
              sx={{
                color: BRAND.ink,
                fontWeight: 500,
                fontSize: '15px',
                textDecoration: 'none',
                '&:hover': { color: BRAND.copperDark },
              }}
            >
              {link.label}
            </MuiLink>
          ))}
        </Box>

        <LanguagePill />
      </Box>
    </Box>
    {/* Spacer for the fixed header */}
    <Box aria-hidden sx={{ height: { xs: 64, md: 72 } }} />
    </>
  );
};

export const LoginHero = ({ card }) => {
  const { t } = useTranslation();
  const points = [t('loginPage.heroPoint1'), t('loginPage.heroPoint2'), t('loginPage.heroPoint3')];
  const stats = [
    { value: '$100M+', label: t('auth.fundedSince2016'), icon: TrendingUpIcon },
    { value: '1,000+', label: t('auth.businessesServed'), icon: BusinessIcon },
    { value: '24-48hrs', label: t('auth.averageApproval'), icon: SpeedIcon },
    { value: '50 States', label: t('auth.nationwideCoverage'), icon: VerifiedIcon },
  ];

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: BRAND.heroGradient,
        py: { xs: 4, md: 8 },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          width: 640,
          height: 640,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(209,146,64,0.22) 0%, transparent 70%)',
          top: -260,
          right: -160,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          ...CONTAINER_SX,
          position: 'relative',
          display: 'grid',
          columnGap: 6,
          rowGap: { xs: 3, md: 3 },
          alignItems: 'center',
          gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
          gridTemplateRows: { md: '1fr auto auto 1fr' },
          gridTemplateAreas: {
            xs: '"intro" "card" "details"',
            md: '". card" "intro card" "details card" ". card"',
          },
        }}
      >
        <Box sx={{ gridArea: 'intro' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.75,
              py: 0.6,
              mb: 2.5,
              borderRadius: '999px',
              border: '1px solid rgba(232,189,118,0.45)',
              background: 'rgba(232,189,118,0.10)',
              color: BRAND.sand,
              fontSize: '12.5px',
              fontWeight: 600,
            }}
          >
            <VerifiedUserIcon sx={{ fontSize: 16 }} />
            {t('loginPage.eyebrow')}
          </Box>
          <Typography
            component="h1"
            sx={{
              color: '#fff',
              fontWeight: 800,
              fontSize: { xs: '2rem', sm: '2.5rem', lg: '3.25rem' },
              lineHeight: 1.08,
              letterSpacing: '-0.02em',
              mb: 2,
            }}
          >
            {t('auth.heroTitle1')}{' '}
            <Box
              component="span"
              sx={{
                background: `linear-gradient(90deg, ${BRAND.sand} 0%, ${BRAND.copperLight} 100%)`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('auth.heroTitle2')}
            </Box>
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontSize: { xs: '15.5px', md: '18px' }, lineHeight: 1.6, maxWidth: '560px' }}>
            {t('auth.heroSubtitle')}
          </Typography>
        </Box>

        <Box sx={{ gridArea: 'card', display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          {card}
        </Box>

        <Box sx={{ gridArea: 'details' }}>
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 4, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {points.map((point) => (
              <Box component="li" key={point} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <CheckCircleIcon sx={{ color: BRAND.sand, fontSize: 20 }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.92)', fontSize: '15px' }}>{point}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            {stats.map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.10)',
                }}
              >
                <stat.icon sx={{ color: BRAND.sand, fontSize: 22, mb: 1 }} />
                <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.35rem', lineHeight: 1.1 }}>
                  {stat.value}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '11.5px', textTransform: 'uppercase', letterSpacing: 0.8, mt: 0.5 }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const HowItWorks = () => {
  const { t } = useTranslation();
  const steps = [
    { icon: PersonAddIcon, title: t('loginPage.step1Title'), desc: t('loginPage.step1Desc') },
    { icon: AssignmentIcon, title: t('loginPage.step2Title'), desc: t('loginPage.step2Desc') },
    { icon: CloudUploadIcon, title: t('loginPage.step3Title'), desc: t('loginPage.step3Desc') },
    { icon: PaidIcon, title: t('loginPage.step4Title'), desc: t('loginPage.step4Desc') },
  ];

  return (
    <Box component="section" id="how-it-works" sx={{ ...SECTION_ANCHOR_SX, background: '#fff', py: { xs: 7, md: 10 } }}>
      <Box sx={CONTAINER_SX}>
        <SectionHeading title={t('loginPage.howTitle')} subtitle={t('loginPage.howSubtitle')} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3 }}>
          {steps.map((step, index) => (
            <Box
              key={step.title}
              sx={{
                position: 'relative',
                p: 3,
                borderRadius: '16px',
                border: `1px solid ${BRAND.border}`,
                background: '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: BRAND.copperLight, boxShadow: '0 10px 30px rgba(72,39,15,0.08)', transform: 'translateY(-2px)' },
              }}
            >
              <Typography
                aria-hidden
                sx={{ position: 'absolute', top: 16, right: 20, fontWeight: 800, fontSize: '2.25rem', color: 'rgba(190,125,55,0.15)', lineHeight: 1 }}
              >
                {index + 1}
              </Typography>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: BRAND.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  boxShadow: '0 6px 14px rgba(172,104,46,0.28)',
                }}
              >
                <step.icon sx={{ color: '#fff', fontSize: 24 }} />
              </Box>
              <Typography component="h3" sx={{ fontWeight: 700, fontSize: '17px', color: BRAND.ink, mb: 1 }}>
                {step.title}
              </Typography>
              <Typography sx={{ color: BRAND.body, fontSize: '14.5px', lineHeight: 1.6 }}>{step.desc}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export const ProgramsGrid = () => {
  const { t } = useTranslation();
  const programs = [
    { icon: AccountBalanceIcon, name: t('loginPage.progSba'), desc: t('loginPage.progSbaDesc') },
    { icon: DescriptionIcon, name: t('loginPage.progTerm'), desc: t('loginPage.progTermDesc') },
    { icon: PrecisionManufacturingIcon, name: t('loginPage.progEquipment'), desc: t('loginPage.progEquipmentDesc') },
    { icon: StorefrontIcon, name: t('loginPage.progWorkingCapital'), desc: t('loginPage.progWorkingCapitalDesc') },
    { icon: CreditCardIcon, name: t('loginPage.progLoc'), desc: t('loginPage.progLocDesc') },
    { icon: ReceiptLongIcon, name: t('loginPage.progInvoice'), desc: t('loginPage.progInvoiceDesc') },
    { icon: GroupsIcon, name: t('loginPage.progPayroll'), desc: t('loginPage.progPayrollDesc') },
    { icon: BoltIcon, name: t('loginPage.progCashAdvance'), desc: t('loginPage.progCashAdvanceDesc') },
  ];

  return (
    <Box component="section" id="programs" sx={{ ...SECTION_ANCHOR_SX, background: BRAND.surface, py: { xs: 7, md: 10 } }}>
      <Box sx={CONTAINER_SX}>
        <SectionHeading title={t('loginPage.programsTitle')} subtitle={t('loginPage.programsSubtitle')} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2.5 }}>
          {programs.map((program) => (
            <Box
              key={program.name}
              sx={{
                p: 2.5,
                borderRadius: '14px',
                background: '#fff',
                border: `1px solid ${BRAND.border}`,
                display: 'flex',
                gap: 1.75,
                alignItems: 'flex-start',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: BRAND.copperLight, boxShadow: '0 8px 24px rgba(72,39,15,0.07)' },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  borderRadius: '10px',
                  background: 'rgba(209,146,64,0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <program.icon sx={{ color: BRAND.copperDark, fontSize: 22 }} />
              </Box>
              <Box>
                <Typography component="h3" sx={{ fontWeight: 700, fontSize: '15.5px', color: BRAND.ink, mb: 0.5 }}>
                  {program.name}
                </Typography>
                <Typography sx={{ color: BRAND.body, fontSize: '13.5px', lineHeight: 1.55 }}>{program.desc}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export const Testimonials = () => {
  const { t } = useTranslation();
  const testimonials = [
    { quote: t('auth.testimonial1Quote'), author: 'Maggie Williams', company: 'Iron Edison Battery', location: 'Denver, CO' },
    { quote: t('auth.testimonial2Quote'), author: 'Desiree Gurule', company: 'Triple S Sanitary Sewer Services', location: t('loginPage.verifiedClient') },
    { quote: t('auth.testimonial3Quote'), author: 'Adriann Johnson', company: 'The CFS Shop', location: t('loginPage.verifiedClient') },
  ];
  const trustBadges = [
    { icon: ShieldIcon, label: t('auth.sslEncryption') },
    { icon: VerifiedUserIcon, label: t('auth.soc2Compliant') },
    { icon: AccountBalanceIcon, label: t('auth.fdicPartnerLenders') },
  ];

  return (
    <Box component="section" sx={{ background: '#fff', py: { xs: 7, md: 10 } }}>
      <Box sx={CONTAINER_SX}>
        <SectionHeading title={t('loginPage.testimonialsTitle')} subtitle={t('loginPage.testimonialsSubtitle')} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
          {testimonials.map((item) => (
            <Box
              key={item.author}
              component="figure"
              sx={{
                m: 0,
                p: 3,
                borderRadius: '16px',
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex' }} aria-label="5 stars">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <StarIcon key={i} sx={{ color: '#F5A623', fontSize: 20 }} />
                  ))}
                </Box>
                <Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: BRAND.muted }}>
                  {t('loginPage.googleReview')}
                </Typography>
              </Box>
              <Box component="blockquote" sx={{ m: 0, mb: 2.5, flex: 1 }}>
                <Typography sx={{ color: '#374151', fontSize: '14.5px', lineHeight: 1.65 }}>
                  &ldquo;{item.quote}&rdquo;
                </Typography>
              </Box>
              <Box component="figcaption" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: BRAND.gradient,
                    color: '#fff',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {item.author.split(' ').map((n) => n[0]).join('')}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '14px', color: BRAND.ink }}>{item.author}</Typography>
                  <Typography sx={{ fontSize: '12.5px', color: BRAND.muted }}>
                    {item.company} • {item.location}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            mt: { xs: 5, md: 7 },
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: { xs: 2, md: 5 },
          }}
        >
          {trustBadges.map((badge) => (
            <Box key={badge.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <badge.icon sx={{ color: BRAND.copperDark, fontSize: 20 }} />
              <Typography sx={{ color: '#374151', fontSize: '14px', fontWeight: 600 }}>{badge.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export const Faq = () => {
  const { t } = useTranslation();
  const faqs = [1, 2, 3, 4, 5].map((n) => ({ q: t(`loginPage.faq${n}Q`), a: t(`loginPage.faq${n}A`) }));

  return (
    <Box component="section" id="faq" sx={{ ...SECTION_ANCHOR_SX, background: BRAND.surface, py: { xs: 7, md: 10 } }}>
      <Box sx={{ ...CONTAINER_SX, maxWidth: '820px' }}>
        <SectionHeading title={t('loginPage.faqTitle')} />
        {faqs.map((faq, index) => (
          <Accordion
            key={faq.q}
            disableGutters
            elevation={0}
            defaultExpanded={index === 0}
            sx={{
              mb: 1.5,
              borderRadius: '12px !important',
              border: `1px solid ${BRAND.border}`,
              background: '#fff',
              '&::before': { display: 'none' },
              '&.Mui-expanded': { borderColor: BRAND.copperLight },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: BRAND.copperDark }} />}
              sx={{ px: 2.5, '& .MuiAccordionSummary-content': { my: 2 } }}
            >
              <Typography component="h3" sx={{ fontWeight: 700, fontSize: '15.5px', color: BRAND.ink }}>
                {faq.q}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
              <Typography sx={{ color: BRAND.body, fontSize: '14.5px', lineHeight: 1.65 }}>{faq.a}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export const FinalCta = ({ onGetStarted }) => {
  const { t } = useTranslation();

  return (
    <Box component="section" sx={{ background: BRAND.heroGradient, py: { xs: 7, md: 9 } }}>
      <Box sx={{ ...CONTAINER_SX, textAlign: 'center', maxWidth: '760px' }}>
        <Typography
          component="h2"
          sx={{ color: '#fff', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2.2rem' }, lineHeight: 1.2, mb: 1.5 }}
        >
          {t('loginPage.ctaTitle')}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: { xs: '15px', md: '17px' }, mb: 4 }}>
          {t('loginPage.ctaSubtitle')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            onClick={onGetStarted}
            variant="contained"
            size="large"
            sx={{
              px: 5,
              py: 1.5,
              borderRadius: '999px',
              background: BRAND.gradient,
              boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
              fontWeight: 700,
              fontSize: '16px',
              textTransform: 'none',
              '&:hover': { background: BRAND.gradientHover },
            }}
          >
            {t('loginPage.ctaButton')}
          </Button>
          <Button
            component="a"
            href={`tel:${SUPPORT_PHONE.replace(/-/g, '')}`}
            variant="outlined"
            size="large"
            startIcon={<PhoneIcon />}
            sx={{
              px: 3.5,
              py: 1.5,
              borderRadius: '999px',
              borderColor: 'rgba(255,255,255,0.4)',
              color: '#fff',
              fontWeight: 600,
              fontSize: '15px',
              textTransform: 'none',
              '&:hover': { borderColor: BRAND.sand, background: 'rgba(255,255,255,0.06)' },
            }}
          >
            {t('loginPage.ctaCall')} {SUPPORT_PHONE}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export const LoginFooter = () => {
  const { t } = useTranslation();
  const linkSx = { color: 'rgba(255,255,255,0.7)', fontSize: '13px', textDecoration: 'none', '&:hover': { color: BRAND.sand } };

  return (
    <Box component="footer" sx={{ background: BRAND.brownDeep, py: 4, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      <Box
        sx={{
          ...CONTAINER_SX,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          textAlign: { xs: 'center', md: 'left' },
        }}
      >
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', mb: 0.5 }}>
            {t('auth.needHelpCall')}{' '}
            <MuiLink href={`tel:${SUPPORT_PHONE.replace(/-/g, '')}`} sx={{ ...linkSx, color: BRAND.sand }}>
              {SUPPORT_PHONE}
            </MuiLink>{' '}
            {t('auth.orEmailUs')}{' '}
            <MuiLink href={`mailto:${SUPPORT_EMAIL}`} sx={{ ...linkSx, color: BRAND.sand }}>
              {SUPPORT_EMAIL}
            </MuiLink>
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
            © {new Date().getFullYear()} Core Business Capital. {t('loginPage.footerRights')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', justifyContent: 'center' }}>
          <MuiLink href={`${MAIN_SITE_URL}/terms`} target="_blank" rel="noopener noreferrer" sx={linkSx}>
            {t('auth.termsOfService')}
          </MuiLink>
          <MuiLink href={`${MAIN_SITE_URL}/privacy`} target="_blank" rel="noopener noreferrer" sx={linkSx}>
            {t('auth.privacyPolicy')}
          </MuiLink>
          <MuiLink href={MAIN_SITE_URL} target="_blank" rel="noopener noreferrer" sx={linkSx}>
            {t('loginPage.visitMainSite')}
          </MuiLink>
        </Box>
      </Box>
    </Box>
  );
};
